<template>
  <div>
    <div class="container">

      <h1 class="se-bp-title">
        Income Type
        <a class="btn-link se-page-help-icon" v-tooltip.top="'Learn More'" @click="showHelp = true">
          <i class="fa fa-info-circle"></i>
        </a>
      </h1>

      <div>
        <radio :label="Enums.SeIncomeTypeType.selfEmployed"
               :disabled="isLocked"
               v-model="tmpIncomeType">Self-Employed or Business Owner
          <a class="btn-link se-help-icon" v-tooltip.top="'Learn More'" @click="showHelpSelfEmployment = true"><i class="fa fa-info-circle"></i></a>
        </radio>

        <radio :label="Enums.SeIncomeTypeType.rental"
               :disabled="isLocked"
               v-model="tmpIncomeType">Rental
          <a class="btn-link se-help-icon" v-tooltip.top="'Learn More'" @click="showHelpRental = true"><i class="fa fa-info-circle"></i></a>
        </radio>

        <radio :label="Enums.SeIncomeTypeType.wageEarner"
               :disabled="isLocked"
               v-model="tmpIncomeType">Wage-earner
          <a class="btn-link se-help-icon" v-tooltip.top="'Learn More'" @click="showHelpWageEarner = true"><i class="fa fa-info-circle"></i></a>
        </radio>

        <div class="bp-help is-error" v-if="v$.tmpIncomeType.$error">
          <div class="bp-ucon" data-src="feedback-warning"><svg class="bp-svg" viewBox="0 0 24 24" id="feedback-warning" xmlns="http://www.w3.org/2000/svg"><path fill="currentColor" d="M13.009 13.987V8.934h-1.956v5.053h1.956zm0 4.016v-1.962h-1.956v1.962h1.956zM2.988 20.962L12.016 3l9.025 17.962H2.988z"></path></svg></div>
          <template v-if="v$.tmpIncomeType.$invalid">Please make a selection</template>
        </div>
    </div>

    <se-help v-model:visible="showHelp" title="Learn More">
      <p>
        Use this screen to tell {{ appName }} the kind of income you want it to evaluate.
      </p>
      <p>
        <icon-base width="22" height="22" iconColor="#0c77ba"><icon-arrow-right/></icon-base>
        Select <b>Self-Employed or Business Owner</b> if you want {{ appName }} to evaluate self-employment or business ownership income from a Sole Proprietorship (Schedule C),
        Partnership, S-Corporation (S-Corp), standard Corporation (C-Corp), or Limited Liability Company (LLC).<br>
		<icon-base width="22" height="22" iconColor="#0c77ba"><icon-arrow-right/></icon-base>
        Select <b>Rental</b> if you want {{ appName }} to evaluate rental property income from a Schedule E, Part I of the borrower's personal tax returns.
      </p>
      <b>FAQs:</b>
      <p>
      Q. Can {{ appName }} evaluate self-employment income or losses generated from rental properties reported to the IRS through a Partnership or S-Corporation?<br>
	    A. Yes, select <b>Self-Employed or Business Owner</b>.
      </p>
      <p>
        Q. Will I need to complete a separate {{ appName }} evaluation for each business?<br>
	    A. Yes. If the borrower has multiple businesses, you will need to complete a separate {{ appName }} evaluation for each business.
        If a business owns one or more rental properties reported on Form 8825, these should be included in the business evaluation.<br>
      </p>
      <p>
        For example, if the borrower is a partner in a tree growing business, is also a partner in a
        landscape design business, and files separate business tax returns for each business, then you
        will need two separate {{ appName }} evaluations; one for each business.
      </p>
       <p>
        Q. Will I need to complete a separate {{ appName }} evaluation for each property reported on Schedule E?<br>
	    A. Yes. If the borrower has multiple rental properties reported on Schedule E, Part I, you will need to complete a separate {{ appName }} evaluation for each property.<br>
      </p>
      <p>
        Q. There are two borrowers on the loan. The borrowers are each 50% owners of the Partnership,
        S-Corporation, Corporation, or LLC. Will I need to complete a separate {{ appName }} evaluation for each borrower?<br>
        A. Yes. Completing separate income evaluations is <b>required</b> for each borrower.
      </p>
    </se-help>

    <se-help v-model:visible="showHelpSelfEmployment" title="Self-Employed or Business Owner">
      <h4>Self-Employed or Business Owner</h4>
      <p>
        Make this selection if the income you want to evaluate is from self-employment or business ownership (including rental property income owned by a business reported on Form 8825).
      </p>
    </se-help>

      <se-help v-model:visible="showHelpRental" title="Rental">
        <h4>Rental</h4>
        <p>
          Make this selection if the income you want to evaluate is only from one or more rental properties reported on Schedule E, Part 1.
        </p>
      </se-help>

    <se-help v-model:visible="showHelpWageEarner" title="Wage-earner">
      <h4>Wage-earner</h4>
      <p>
        Select <b>Wage-earner</b> if you want {{ appName }} to evaluate income reported on Form W-2 or on year-to-date/year-end paystubs, for one or more employments.
      </p>
    </se-help>

    <se-buttons
      :display-save="false"
      :display-submit="false"
      @seNext="next"
      @sePrevious="previous">
    </se-buttons>
  </div>
  </div>

</template>
<script>
import { defineComponent, getCurrentInstance, computed} from 'vue';

import {Radio, IconBook, IconArrowRight} from 'src/components/index'
import LSwitch from 'src/components/Switch.vue'
import SeButtons from './SeButtons.vue'
import Swal from 'sweetalert2/dist/sweetalert2'
import {mapGetters, mapState} from 'vuex'
import {ElSelect, ElOption, ElDatePicker} from 'element-plus'
import Enums from 'src/model/enums'
import {
  RETAIN_BUS_STRUCT_COMPATIBLE_TAX_FORMS,
  RETAIN_TAX_DATA,
  SET_EXTRA_DATA,
  SET_REQUEST_DATA,
  ADD_WAGE_EMPLOYMENT,
  SET_WAGE_EMPLOYMENT_DATA
} from 'src/store/actions/se'
import SeHelp from './SeHelp.vue'
import {required, requiredIf} from '@vuelidate/validators'
import appNameMixin from '../../../mixins/AppNameMixin'
import {getConditionalWagesFieldName} from "@/util/seYearSpecific";
import {hasNumericValue, hasPositiveNumericValue} from "@/util/seutil";
import { useVuelidate } from '@vuelidate/core'

export default defineComponent({
  mixins: [appNameMixin],

  components: {
    Radio,
    LSwitch,
    SeButtons,
    SeHelp,
    IconBook, IconArrowRight,
    [ElSelect.name]: ElSelect,
    [ElOption.name]: ElOption,
    [ElDatePicker.name]: ElDatePicker
  },

  setup() {
    const instance = getCurrentInstance()
    const validationsDef = instance.type.validations
    const rulesRef = computed(() =>
      typeof validationsDef === 'function'
        ? validationsDef.call(instance.proxy)
        : validationsDef
    )
    return { v$: useVuelidate(rulesRef, instance.proxy) }
  },

  data() {
    return {
      tmpIncomeType: null,
      showHelp: false,
      showHelpScheduleEPart1: false,
      showHelpBusinessTaxReturns: false,
      showHelpSelfEmployment: false,
      showHelpRental: false,
      showHelpWageEarner: false
    }
  },

  validations: {
    tmpIncomeType: {
      required: required
    }
  },

  created() {
    this.Enums = Enums
    this.tmpIncomeType = this.incomeType
    this.ensureFirstEmploymentExists()
  },

  watch: {
    // Auto-create "Employment 1" as soon as the user picks Wage-earner (even before
    // clicking Continue) so the details section below can bind to it right away.
    tmpIncomeType(newVal) {
      if (newVal === Enums.SeIncomeTypeType.wageEarner) {
        this.ensureFirstEmploymentExists()
      }
    }
  },

  computed: {
    incomeType: {
      get () {
        return this.seRequest.incomeType
      },
      set (incomeType) {
        this.$store.commit(SET_REQUEST_DATA, {incomeType})

        if (incomeType === Enums.SeIncomeTypeType.selfEmployed) {
          this.$store.commit(SET_EXTRA_DATA, {rentalIncomeIrsFormType: null})
          if (this.seRequest.extraData.businessStructureProvided === Enums.SeBusinessStructureType.rental.id) {
            this.$store.commit(SET_EXTRA_DATA, {businessStructureProvided: Enums.SeBusinessStructureType.soleProprietorship.id})
            this.$store.commit(RETAIN_BUS_STRUCT_COMPATIBLE_TAX_FORMS)
          }

        } else if (incomeType === Enums.SeIncomeTypeType.rental) {
          this.$store.commit(SET_EXTRA_DATA, {rentalIncomeIrsFormType: Enums.SeRentalIncomeIrsFormType.scheduleEPart1})

          if (this.seRequest.extraData.businessStructureProvided !== Enums.SeBusinessStructureType.rental.id) {
            this.$store.commit(SET_EXTRA_DATA, {businessStructureProvided: Enums.SeBusinessStructureType.rental.id})
            this.$store.commit(RETAIN_BUS_STRUCT_COMPATIBLE_TAX_FORMS)
          }
        }
      }
    },
    ...mapState({
      seRequest: state => state.se.request
    }),
    ...mapGetters([
      'isLocked',
      'getPY1',
      'getPY2'
    ])
  },

  methods: {
    next() {
      this.v$.$touch();
      if (this.v$.$invalid) {
        return;
      }

      if (this.tmpIncomeType !== null
        && this.tmpIncomeType !== this.incomeType
        && (this.seRequest.requestId || this.isTaxableIncomePresent())) {
        this.alertIncomeTypeChanged()
        return;
      }

      this.incomeType = this.tmpIncomeType
      this.navigateAfterIncomeTypeSet()
    },
    previous() {
      this.$router.push('start')
    },

    // Routes to the correct next screen based on the selected income type. For Wage Earner,
    // ensures "Employment 1" is auto-created so the sidebar never shows an empty Employments folder,
    // but still routes through Reference Name / Length of Employment / DU like other income types.
    navigateAfterIncomeTypeSet() {
      if (this.tmpIncomeType === Enums.SeIncomeTypeType.wageEarner) {
        this.ensureFirstEmploymentExists()
      }
      this.$router.push('business-name')
    },

    ensureFirstEmploymentExists() {
      if (!this.seRequest.wageData || this.seRequest.wageData.length === 0) {
        this.$store.commit(ADD_WAGE_EMPLOYMENT)
      }
      const emp = this.seRequest.wageData && this.seRequest.wageData[0]?.employment
      if (emp) {
        const defaults = {}
        for (const field of ['contractBasisIndicator', 'specialRelationshipIndicator', 'foreignIncomeIndicator', 'virtualCurrencyIndicator']) {
          if (emp[field] === null || emp[field] === undefined) {
            defaults[field] = false
          }
        }
        if (!emp.employmentStatus) {
          defaults.employmentStatus = 'current'
        }
        if (Object.keys(defaults).length > 0) {
          this.$store.commit(SET_WAGE_EMPLOYMENT_DATA, {employmentIdx: 0, fields: defaults})
        }
      }
    },

    isTaxableIncomePresent() {
      return [this.seRequest.taxData.find(form => form.year === this.getPY1 && form.formId === Enums.SeTaxFormType.tf1040)?.fields,
              this.seRequest.taxData.find(form => form.year === this.getPY1 && form.formId === Enums.SeTaxFormType.tf1040sr)?.fields,
              this.seRequest.taxData.find(form => form.year === this.getPY1 && form.formId === Enums.SeTaxFormType.tf1040nr)?.fields]
        .some(fields => hasNumericValue(fields, 'taxableIncome'))
    },

    alertIncomeTypeChanged() {
      let msgIcon = "warning"
      let msgHtml = "<p>All rental and business data will be permanently removed. Individual (personal) tax return data will be retained.</p>"

      Swal.fire({
        title: 'WARNING!',
        html: msgHtml,
        width: 480,
        showCancelButton: true,
        buttonsStyling: false,
        customClass: {confirmButton: 'bp-button bp-button--primary', cancelButton: 'bp-button'},
        confirmButtonText: `Change Income Type`,
        cancelButtonText: `Cancel`,
        icon: msgIcon
      }).then((result) => {
        if (result.isConfirmed) {
          this.incomeType = this.tmpIncomeType
          this.$store.commit(RETAIN_TAX_DATA, [
            {'year': this.getPY1, 'id': Enums.SeTaxFormType.tf1040},
            {'year': this.getPY1, 'id': Enums.SeTaxFormType.tf1040sr},
            {'year': this.getPY1, 'id': Enums.SeTaxFormType.tf1040nr},
            {'year': this.getPY1, 'id': Enums.SeTaxFormType.tf1040s1},
            {'year': this.getPY2, 'id': Enums.SeTaxFormType.tf1040},
            {'year': this.getPY2, 'id': Enums.SeTaxFormType.tf1040sr},
            {'year': this.getPY2, 'id': Enums.SeTaxFormType.tf1040nr},
            {'year': this.getPY2, 'id': Enums.SeTaxFormType.tf1040s1},
          ])
          this.navigateAfterIncomeTypeSet()
        }
      })
    }

  },
});
</script>
